// "Why this matters" for the keeper's to-do list. Each entry is matched to
// a task by words in its title, so a renamed or hand-added task ("Top off
// ATO") still gets the right card. First match wins, so specific entries
// sit above general ones.
//
// Copy rules (owner, 2026-09-26): say plainly what the task does and what
// happens if it is skipped. No guilt, no scolding, no invented numbers
// (docs/ANTI-PATTERNS.md #13). The live copy is the admin-editable
// `task_explainers` config; this is its default and the app's offline
// fallback, so there is one source for both.
//
// Fields per item:
//   key       stable id
//   priority  1 protects the tank, 7 is nice to have: what a reminder
//             leads with when it can only show a few
//   match   words or phrases looked for in the task title (whole words)
//   kind    'test' | 'chore'. A test shows when the param was last read.
//   params  reading keys whose current value is worth showing on the card
//   action  'log' | 'icp' | 'water_change' | null: the one-tap next step
//   why / skip / how   the copy
//   fresh   { why, skip, how, params } for a freshwater tank, each falling
//           back to the reef one; null = the item is about a reef only, so
//           a freshwater tank shows no card for it

import { tankSubtype } from './tank-subtype.js';
import { FRESHWATER_SUBTYPES } from './param-explainers.js';

export const DEFAULT_TASK_EXPLAINERS = {
  items: [
    {
      key: 'water_change',
      priority: 1,
      match: ['water change', 'wc'],
      kind: 'chore',
      params: ['nitrate', 'phosphate'],
      action: 'water_change',
      why: 'Removes the nitrate, phosphate and other waste that build up between changes, and puts back the trace elements your corals and fish use up.',
      skip: 'Waste keeps building and trace elements keep dropping. The change is slow, which is why it is easy to miss until corals lose colour or algae takes off.',
      how: [
        "Mix the new salt water a day ahead so it's heated and fully dissolved.",
        'Match its salinity and temperature to the tank before it goes in.',
        'Siphon from the sand and rockwork, where detritus settles.',
        'Refill slowly, away from corals.',
        'Log it here so your salt count and next date update.',
      ],
      fresh: {
        why: 'Removes the nitrate and other waste that build up between changes, and brings back the minerals that hold pH steady.',
        skip: "Waste keeps building and the water's buffer keeps getting used up. The change is slow, which is why it is easy to miss until fish look stressed or algae takes off.",
        how: [
          'Treat tap water with a water conditioner before it goes in: chlorine and chloramine harm fish and the bacteria in the filter.',
          'Match its temperature to the tank.',
          'Vacuum the gravel, where waste settles, and go gently around plant roots.',
          'Refill slowly so the gravel and plants stay put.',
          'Log it here so your next date updates.',
        ],
      },
    },
    {
      key: 'ato',
      priority: 1,
      match: ['ato', 'top off', 'top-off', 'topoff', 'top up', 'reservoir'],
      kind: 'chore',
      params: ['salinity'],
      action: null,
      why: "Water evaporates, salt doesn't. The ATO replaces the lost water with fresh RO/DI so salinity stays where it is.",
      skip: 'Once the reservoir runs dry, salinity climbs a little every day until someone notices. A dry reservoir can also leave the pump running dry, which damages many ATO pumps.',
      how: [
        'Refill with RO/DI water only. Tap water brings phosphate, silicate and metals.',
        'Never top off with salt water: it raises salinity instead of holding it.',
        "Check the sensor is clean and the float or optical eye isn't stuck.",
        'Note how long a full reservoir lasts, and set this task to match.',
      ],
      fresh: {
        why: 'Water evaporates, and what was dissolved in it stays behind. Topping off keeps the level up so the heater and the filter intake stay under water.',
        skip: 'The level drops a little every day, and the water left behind slowly gets harder. A dry reservoir can also leave the pump running dry, which damages many top-off pumps.',
        how: [
          'Top off with tap water treated with a conditioner, or with plain RO or distilled water if you add minerals to RO for your water changes: only water evaporates.',
          "If you use an auto top-off, check the sensor is clean and the float or optical eye isn't stuck.",
          'If you use a reservoir, note how long a full one lasts, and set this task to match.',
        ],
      },
    },
    {
      key: 'glass',
      priority: 6,
      match: ['glass', 'acrylic', 'algae magnet', 'scrape'],
      kind: 'chore',
      params: [],
      action: null,
      why: "A film that grows back quickly is an early sign of extra nutrients. Clear glass is also how you notice a fish that isn't eating or a coral that stays closed.",
      skip: 'The film thickens and coralline algae sets in, and then it needs a scraper instead of a wipe.',
      how: [
        'Use a magnet or a pad kept only for the tank.',
        'Start at the top and work down.',
        'Keep sand out of the pad: one grain between pad and glass scratches acrylic.',
        'Do the sides and back if you can reach them. They grow the same film.',
      ],
      fresh: {
        why: "A film that grows back quickly is a sign that light and nutrients are out of balance. Clear glass is also how you notice a fish that isn't eating or is acting differently.",
        skip: 'The film thickens, and some algae, like green spot algae, then needs a scraper instead of a wipe.',
        how: [
          'Use a magnet or a pad kept only for the tank.',
          'Start at the top and work down.',
          'Keep gravel and sand out of the pad: one grain between pad and glass scratches acrylic.',
          'Do the sides and back if you can reach them. They grow the same film.',
        ],
      },
    },
    {
      key: 'filter_sock',
      priority: 4,
      match: ['filter sock', 'sock', 'filter floss', 'floss', 'filter roll', 'roller'],
      kind: 'chore',
      params: ['nitrate', 'phosphate'],
      action: null,
      why: "A sock traps waste before it breaks down. Once it's full, that waste keeps breaking down in the sump and feeds nitrate and phosphate.",
      skip: 'A full sock overflows around the rim, so water bypasses it and the waste goes straight back into the system.',
      how: [
        'Swap in a clean sock rather than rinsing the dirty one over the sump.',
        'Rinse the dirty one outside the tank.',
        'Wash socks with no detergent or fabric softener, rinse well and let them dry.',
        'Keep a spare set so a clean one is always ready.',
      ],
      fresh: {
        why: 'Floss traps waste before it breaks down. Once it is clogged, that waste keeps breaking down inside the filter and feeds nitrate.',
        skip: 'Clogged floss slows the flow, and water finds a way around it, taking the waste back into the tank.',
        how: [
          'Fine floss is usually replaced, not rinsed.',
          'Rinse sponges in water taken out of the tank, never under the tap: chlorine kills the bacteria the filter needs.',
          'Clean one part of the filter at a time, not all of it on the same day.',
        ],
      },
    },
    {
      key: 'pumps',
      priority: 5,
      match: ['pump', 'pumps', 'powerhead', 'powerheads', 'wavemaker'],
      kind: 'chore',
      params: [],
      action: null,
      why: 'Calcium deposits and coralline build up inside pumps and powerheads. A scaled pump moves less water and works harder.',
      skip: 'Flow drops slowly, detritus settles in dead spots, and a clogged pump can seize.',
      how: [
        'Unplug it first.',
        'Soak the housing and impeller in vinegar or a citric acid solution to loosen deposits.',
        'Brush the impeller and check the shaft for wear.',
        'Rinse well in fresh water before it goes back in.',
      ],
      fresh: {
        why: 'Mineral scale and sludge build up inside pumps, powerheads and filter impellers. A clogged pump moves less water and works harder.',
      },
    },
    {
      key: 'icp',
      priority: 4,
      match: ['icp'],
      kind: 'chore',
      params: [],
      action: 'icp',
      why: 'A test kit covers a handful of elements. An ICP measures dozens, including trace elements and metals that no hobby kit tests.',
      skip: 'Slow changes, like iodine falling or a metal creeping up, stay unseen until they show in your corals.',
      how: [
        "Order the kit ahead so it's here on test day.",
        'Take the sample before a water change or dosing, not right after.',
        "Follow the lab's instructions for filling and posting.",
        'Upload the result here to see what changed since last time.',
      ],
      fresh: {
        why: 'A test kit covers a handful of things. An ICP measures dozens of elements, including metals that most hobby kits do not test.',
        skip: 'Slow changes, like a metal creeping up, stay unseen until they show in your fish or plants.',
        how: [
          "Order the kit ahead so it's here on test day.",
          'Take the sample before a water change, not right after.',
          "Follow the lab's instructions for filling and posting.",
          'Upload the result here to see what changed since last time.',
        ],
      },
    },
    {
      key: 'tank_photo',
      priority: 7,
      match: ['photo', 'picture'],
      kind: 'chore',
      params: [],
      action: null,
      why: 'Coral growth and colour change too slowly to notice day to day. A photo from the same spot each week makes them visible.',
      skip: "You lose the comparison. By the time a change is obvious, it's hard to say when it started.",
      how: [
        'Take it from the same spot each time.',
        'Use the same lighting each time, not a mix.',
        'Turn the pumps off for a moment so the surface is still.',
        'Add it to your photo timeline on Home.',
      ],
      fresh: {
        why: 'Plants grow and algae creeps in too slowly to notice day to day. A photo from the same spot each week makes the change visible.',
        how: [
          'Take it from the same spot each time.',
          'Use the same lighting each time, not a mix.',
          'Add it to your photo timeline on Home.',
        ],
      },
    },
    {
      key: 'cleanup_crew',
      priority: 5,
      match: ['cleanup crew', 'clean-up crew', 'clean up crew', 'cuc'],
      kind: 'chore',
      params: [],
      action: null,
      why: 'Snails and hermit crabs graze algae and eat leftover food before it rots, which helps keep nutrients down.',
      skip: 'Leftover food and algae build up faster, and you end up doing that work by hand.',
      how: [
        'Add a few at a time: there has to be enough algae and food for them already.',
        'Acclimate them the way you would a fish.',
        'Pick species that suit your rock and sand.',
        'Add them to Livestock so the app knows what lives in the tank.',
      ],
      fresh: {
        why: 'Snails and shrimp graze algae and eat leftover food before it rots.',
        how: [
          'Add a few at a time: there has to be enough algae and food for them already.',
          'Acclimate them slowly: shrimp are usually drip-acclimated, more slowly than fish.',
          'Pick species your fish leave alone: some fish eat small snails and shrimp.',
          'Add them to Livestock so the app knows what lives in the tank.',
        ],
      },
    },
    {
      key: 'cycle',
      priority: 1,
      match: ['ammonia', 'nitrite'],
      kind: 'test',
      params: ['ammonia', 'nitrite'],
      action: 'log',
      why: 'While a tank cycles, bacteria turn ammonia into nitrite and then into nitrate. Testing shows how far along the cycle is.',
      skip: "You can't tell when the cycle has finished, and that decides when it's safe to add livestock.",
      how: [
        'Test ammonia and nitrite together.',
        'Log both here so the cycle shows its progress.',
        'Wait until both read zero for several days in a row before adding livestock.',
      ],
    },
    {
      key: 'nutrients',
      priority: 3,
      match: ['nitrate', 'phosphate', 'no3', 'po4', 'nutrients'],
      kind: 'test',
      params: ['nitrate', 'phosphate'],
      action: 'log',
      why: 'Nitrate and phosphate are what remains of food and waste. Corals need a little of both: too much feeds algae, too little starves corals.',
      skip: 'Nutrients drift unseen, and the first sign becomes algae or pale corals instead of a number.',
      how: [
        'Test both on the same day so you can compare them.',
        "Follow the kit's timing exactly; low-range tests are sensitive to it.",
        'Log them here so your trend stays current.',
      ],
      fresh: {
        why: "Nitrate is what remains of food and fish waste once the filter's bacteria have broken it down. It builds up between water changes unless plants use it, and too much feeds algae and stresses fish.",
        skip: 'Nitrate creeps up unseen, and the first sign becomes algae or stressed fish instead of a number.',
        how: [
          "Follow the kit's steps and timing exactly.",
          'Test before a water change, not right after, so you see the real level.',
          'Log it here so your trend stays current.',
        ],
      },
    },
    {
      key: 'calcium_magnesium',
      priority: 3,
      match: ['calcium + magnesium', 'calcium and magnesium', 'ca/mg', 'ca + mg'],
      kind: 'test',
      params: ['calcium', 'magnesium'],
      action: 'log',
      why: 'Corals build their skeletons from calcium, and magnesium keeps calcium and alkalinity in solution. When magnesium is low, the other two are hard to hold steady.',
      skip: 'Calcium and alkalinity drift and are hard to correct, and dosing without a recent test is guesswork.',
      how: [
        "Use fresh reagents and follow each kit's timing.",
        'Test both on the same day.',
        'Log them here so your dose calculation uses current numbers.',
      ],
      fresh: null,
    },
    {
      key: 'alk',
      priority: 2,
      match: ['alkalinity', 'alk', 'dkh', 'kh'],
      kind: 'test',
      params: ['alk'],
      action: 'log',
      why: 'Corals use alkalinity to build their skeletons, so it drops as they grow. Keeping it steady is one of the biggest factors in coral health.',
      skip: 'A slow decline goes unnoticed, and a big correction later is harder on corals than the decline was.',
      how: [
        'Test at the same time of day each time; alkalinity moves over the day.',
        "Use a fresh reagent and follow the kit's timing.",
        'Log it here so your trend and dose calculation stay current.',
      ],
      fresh: {
        params: ['kh'],
        why: "KH is the buffer that holds pH steady. The filter's bacteria use it up as they break down waste, so it falls between water changes.",
        skip: 'KH drifts unseen, and in a tank that relies on it to hold pH, pH can drop quickly once it runs out.',
        how: [
          "Count the drops until the colour changes, and follow the kit's steps.",
          'Log it here so your trend stays current.',
        ],
      },
    },
    {
      key: 'calcium',
      priority: 3,
      match: ['calcium', 'ca'],
      kind: 'test',
      params: ['calcium'],
      action: 'log',
      why: 'Corals, coralline algae and clams take calcium from the water to build their skeletons, alongside alkalinity.',
      skip: 'Low calcium slows growth, and dosing without a recent test is guesswork.',
      how: [
        "Use a fresh reagent and follow the kit's timing.",
        'Log it here so your dose calculation uses a current number.',
      ],
      fresh: null,
    },
    {
      key: 'magnesium',
      priority: 3,
      match: ['magnesium', 'mg'],
      kind: 'test',
      params: ['magnesium'],
      action: 'log',
      why: 'Magnesium keeps calcium and alkalinity in solution. When it runs low, those two are hard to hold steady.',
      skip: "Alkalinity and calcium drift and won't hold, and it's easy to chase them without checking magnesium.",
      how: [
        "Use a fresh reagent and follow the kit's timing.",
        'Log it here so your trend stays current.',
      ],
      fresh: null,
    },
    {
      key: 'salinity',
      priority: 2,
      match: ['salinity', 'refractometer', 'specific gravity', 'sg'],
      kind: 'test',
      params: ['salinity'],
      action: 'log',
      why: 'Salinity affects everything else in the tank. Evaporation and water changes shift it slowly, so it drifts without anyone noticing.',
      skip: 'Corals and inverts are sensitive to salinity swings, and a refractometer that has drifted can hide a real change.',
      how: [
        'Calibrate the refractometer with calibration fluid, not RO water.',
        'Take the sample from the display, not the sump.',
        'Log it here.',
      ],
      fresh: null,
    },
  ],
};

function escapeRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Whole-word, case-insensitive: "ato" matches "Refill ATO" but not "potato",
// and "ca" matches "Test Ca" but not "cartridge".
function termMatches(title, term) {
  const t = String(term || '').trim();
  if (!t) return false;
  return new RegExp(`(^|[^a-z0-9])${escapeRe(t.toLowerCase())}($|[^a-z0-9])`).test(title);
}

// The explainer for a task title, or null. `cfg` is the live
// task_explainers config; a missing or broken one falls back to the default.
// A quarantine or treatment course's daily check carries the course name
// in its title ("Treatment check: ammonia burn on the clown"), and any
// word in that name could match an explainer: a logged ammonia reading
// would then tick off the check. These tasks belong to their course.
export const COURSE_CHECK_TITLE = /^(quarantine|treatment) check:/i;
// A follow-up's task ("Follow up: Check Alkalinity") belongs to its care
// plan the same way: read as an alk test, the next alk reading deleted it
// and the plan stayed open with nothing to bring the keeper back.
export const FOLLOW_UP_TITLE = /^follow up:/i;

export function matchTaskExplainer(title, cfg) {
  const items = Array.isArray(cfg?.items) ? cfg.items : DEFAULT_TASK_EXPLAINERS.items;
  const t = String(title || '').toLowerCase();
  if (!t || COURSE_CHECK_TITLE.test(t) || FOLLOW_UP_TITLE.test(t)) return null;
  for (const item of items) {
    if (!item || !Array.isArray(item.match)) continue;
    if (item.match.some((m) => termMatches(t, m))) return item;
  }
  return null;
}

// The copy a tank reads. Feedback #74 (2026-10-15): a community tank's
// chores were sent "pale corals", "coralline algae" and "mix the new salt
// water" every day, by push and in the task email. A freshwater tank gets
// the item's `fresh` copy, or no card for an item about a reef only. An
// admin-edited item saved before `fresh` existed takes the shipped one by
// key; an item the admin added themselves keeps its words.
const DEFAULT_FRESH = new Map(
  DEFAULT_TASK_EXPLAINERS.items.filter((it) => 'fresh' in it).map((it) => [it.key, it.fresh])
);

function isFreshwaterTank(tank) {
  return FRESHWATER_SUBTYPES.includes(String(tankSubtype(tank) || ''));
}

function freshFor(item, tank) {
  if (!item || !isFreshwaterTank(tank)) return undefined;
  return Object.hasOwn(item, 'fresh') ? item.fresh : DEFAULT_FRESH.get(item.key);
}

export function explainerForTank(item, tank) {
  const fresh = freshFor(item, tank);
  if (fresh === undefined) return item;
  if (!fresh) return null;
  return {
    ...item,
    params: Array.isArray(fresh.params) ? fresh.params : item.params,
    why: fresh.why || item.why,
    skip: fresh.skip || item.skip,
    how: Array.isArray(fresh.how) ? fresh.how : item.how,
  };
}

// The cards an item's test covers on this tank, card or no card for its
// copy: a freshwater "Test KH" is the KH card, not alkalinity, which the
// tank hides (so it was never reminded, and a logged KH never ticked it).
export function explainerParams(item, tank) {
  const fresh = freshFor(item, tank);
  if (fresh && Array.isArray(fresh.params)) return fresh.params;
  return Array.isArray(item?.params) ? item.params : [];
}

// Server-side check for an admin edit. Returns an error string or null.
export function validateTaskExplainers(v) {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return 'Must be an object.';
  if (!Array.isArray(v.items)) return 'Needs an "items" array.';
  const keys = new Set();
  for (const [i, it] of v.items.entries()) {
    const at = `items[${i}]`;
    if (!it || typeof it !== 'object') return `${at} must be an object.`;
    if (typeof it.key !== 'string' || !it.key) return `${at} needs a key.`;
    if (keys.has(it.key)) return `${at}: key "${it.key}" is used twice.`;
    keys.add(it.key);
    if (!Array.isArray(it.match) || !it.match.length || it.match.some((m) => typeof m !== 'string'))
      return `${at} needs a "match" list of words.`;
    if (typeof it.why !== 'string' || !it.why.trim()) return `${at} needs "why".`;
    if (typeof it.skip !== 'string' || !it.skip.trim()) return `${at} needs "skip".`;
    if (!Array.isArray(it.how) || it.how.some((s) => typeof s !== 'string'))
      return `${at} "how" must be a list of steps.`;
    if (
      it.params != null &&
      (!Array.isArray(it.params) || it.params.some((p) => typeof p !== 'string'))
    )
      return `${at} "params" must be a list of parameter keys.`;
    if (it.kind != null && it.kind !== 'test' && it.kind !== 'chore')
      return `${at} "kind" must be "test" or "chore".`;
    if (
      it.priority != null &&
      !(Number.isInteger(it.priority) && it.priority >= 1 && it.priority <= 9)
    )
      return `${at} "priority" must be a whole number from 1 to 9.`;
    if (it.action != null && !['log', 'icp', 'water_change'].includes(it.action))
      return `${at} "action" must be log, icp, water_change or null.`;
    if (it.fresh != null) {
      const f = it.fresh;
      if (typeof f !== 'object' || Array.isArray(f))
        return `${at} "fresh" must be an object with why, skip and how, or null.`;
      for (const k of ['why', 'skip'])
        if (f[k] != null && typeof f[k] !== 'string') return `${at} "fresh.${k}" must be text.`;
      if (f.how != null && (!Array.isArray(f.how) || f.how.some((s) => typeof s !== 'string')))
        return `${at} "fresh.how" must be a list of steps.`;
      if (
        f.params != null &&
        (!Array.isArray(f.params) || f.params.some((p) => typeof p !== 'string'))
      )
        return `${at} "fresh.params" must be a list of parameter keys.`;
    }
  }
  return null;
}
